import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, AlertCircle, ChevronRight, Star } from 'lucide-react';
import { SCENARIOS } from '../data/scenarios';

const PATIENT_IMAGES = {
  default: 'https://images.unsplash.com/photo-1765031092161-a9ebe556117e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxwaGFybWFjeSUyMGN1c3RvbWVyfGVufDB8fHx8MTc3MjI3ODgxN3ww&ixlib=rb-4.1.0&q=85&w=400',
  pharmacist: 'https://images.unsplash.com/photo-1576091358783-a212ec293ff3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxwaGFybWFjaXN0JTIwY29uc3VsdGF0aW9ufGVufDB8fHx8MTc3MjI3ODgwNXww&ixlib=rb-4.1.0&q=85&w=400',
};

const EMOTION_ICONS = {
  neutral: '😐',
  happy: '😊',
  confused: '😕',
  interested: '🤔',
  sad: '😢',
};

const QUALITY_CONFIG = {
  correct: {
    icon: CheckCircle2,
    color: 'hsl(145 65% 38%)',
    bg: 'hsl(145 65% 94%)',
    border: 'hsl(145 65% 38% / 0.3)',
    label: 'Отлично',
    scorePrefix: '+',
  },
  wrong: {
    icon: XCircle,
    color: 'hsl(0 72% 51%)',
    bg: 'hsl(0 72% 95%)',
    border: 'hsl(0 72% 51% / 0.3)',
    label: 'Ошибка',
    scorePrefix: '',
  },
  neutral: {
    icon: AlertCircle,
    color: 'hsl(38 90% 45%)',
    bg: 'hsl(38 90% 94%)',
    border: 'hsl(38 90% 50% / 0.3)',
    label: 'Можно лучше',
    scorePrefix: '+',
  },
};

const DialogScene = ({ onComplete }) => {
  const [currentSceneId, setCurrentSceneId] = useState('start');
  const [score, setScore] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [history, setHistory] = useState([]);
  const [scoreBump, setScoreBump] = useState(false);

  const scene = SCENARIOS[currentSceneId];
  const totalScenes = 4;
  const progress = ((scene.scene - 1) / totalScenes) * 100;

  const handleChoice = (choice, index) => {
    if (selectedChoice !== null) return;
    setSelectedChoice(index);
    setShowFeedback(true);

    const newScore = score + choice.score;
    setScore(newScore);
    setScoreBump(true);
    setTimeout(() => setScoreBump(false), 600);

    setHistory(prev => [...prev, { sceneId: currentSceneId, choiceIndex: index, score: choice.score, finalScore: newScore }]);
  };

  const handleNext = () => {
    const choice = scene.choices[selectedChoice];
    const currentFinalScore = history.length > 0 ? history[history.length - 1].finalScore : score;
    if (choice.next === 'result') {
      onComplete(score);
      return;
    }
    setSelectedChoice(null);
    setShowFeedback(false);
    setCurrentSceneId(choice.next);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'hsl(var(--background))' }}>
      {/* Top bar */}
      <div
        className="sticky top-0 z-20 px-5 py-3 flex items-center justify-between"
        style={{
          background: 'hsl(var(--card))',
          borderBottom: '1.5px solid hsl(var(--border))',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium" style={{ color: 'hsl(var(--muted-foreground))' }}>
            Сцена {scene.scene} из {totalScenes} — Консультация в аптеке
          </p>
          <div className="progress-track w-36">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <motion.div
          animate={scoreBump ? { scale: [1, 1.3, 1] } : {}}
          transition={{ duration: 0.4 }}
          className="score-badge"
        >
          <Star size={12} />
          <span>{score < 0 ? score : `+${score}`}</span>
          <span style={{ opacity: 0.7 }}>баллов</span>
        </motion.div>
      </div>

      {/* Scene content */}
      <div className="flex-1 px-5 py-6 max-w-2xl w-full mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSceneId}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.35 }}
          >
            {/* Patient card */}
            <div
              className="rounded-2xl overflow-hidden mb-5"
              style={{
                background: 'hsl(var(--card))',
                border: '1.5px solid hsl(var(--border))',
                boxShadow: 'var(--shadow-md)',
              }}
            >
              <div className="flex items-stretch">
                {/* Patient photo */}
                <div
                  className="w-24 sm:w-32 flex-shrink-0 relative"
                  style={{ minHeight: 160 }}
                >
                  <img
                    src={PATIENT_IMAGES.default}
                    alt="Пациент"
                    className="w-full h-full object-cover object-center"
                    style={{ minHeight: 160 }}
                  />
                  {/* Emotion overlay */}
                  <div
                    className="absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-lg"
                    style={{
                      background: 'hsl(var(--card))',
                      border: '2px solid hsl(var(--border))',
                      boxShadow: 'var(--shadow-sm)',
                    }}
                  >
                    {EMOTION_ICONS[scene.patientEmotion] || '😐'}
                  </div>
                </div>

                {/* Dialog bubble */}
                <div className="flex-1 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ background: 'var(--gradient-ocean)' }}
                      />
                      <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'hsl(var(--muted-foreground))' }}>
                        Пациент
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed" style={{ color: 'hsl(var(--foreground))' }}>
                      {scene.patientText}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pharmacist hint */}
              <div
                className="px-5 py-3 border-t flex items-center gap-2"
                style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--accent) / 0.4)' }}
              >
                <img
                  src={PATIENT_IMAGES.pharmacist}
                  alt="Провизор"
                  className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                  style={{ border: '1.5px solid hsl(var(--primary-light))' }}
                />
                <p className="text-xs font-medium" style={{ color: 'hsl(var(--accent-foreground))' }}>
                  <span className="font-semibold">Вы (провизор):</span> {scene.pharmacistHint}
                </p>
              </div>
            </div>

            {/* Choices */}
            <div className="flex flex-col gap-3 mb-5">
              {scene.choices.map((choice, index) => {
                const isSelected = selectedChoice === index;
                let btnClass = 'choice-btn';
                if (showFeedback && isSelected) {
                  btnClass += ` choice-btn-${choice.quality}`;
                }

                return (
                  <motion.button
                    key={index}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.08 }}
                    onClick={() => handleChoice(choice, index)}
                    disabled={showFeedback}
                    className={btnClass}
                    style={{ cursor: showFeedback ? 'default' : 'pointer' }}
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5"
                        style={{
                          background: showFeedback && isSelected
                            ? 'transparent'
                            : 'hsl(var(--muted))',
                          color: 'hsl(var(--muted-foreground))',
                        }}
                      >
                        {showFeedback && isSelected ? (
                          choice.quality === 'correct'
                            ? <CheckCircle2 size={16} color="hsl(145 65% 38%)" />
                            : choice.quality === 'wrong'
                            ? <XCircle size={16} color="hsl(0 72% 51%)" />
                            : <AlertCircle size={16} color="hsl(38 90% 45%)" />
                        ) : String.fromCharCode(65 + index)}
                      </span>
                      <span>{choice.text}</span>
                    </div>

                    {/* Score chip */}
                    {showFeedback && isSelected && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="absolute top-3 right-4 text-xs font-semibold px-2 py-0.5 rounded-full"
                        style={{
                          background: choice.score > 0 ? 'hsl(145 65% 38%)' : choice.score < 0 ? 'hsl(0 72% 51%)' : 'hsl(38 90% 50%)',
                          color: 'hsl(0 0% 100%)',
                        }}
                      >
                        {choice.score > 0 ? `+${choice.score}` : choice.score}
                      </motion.span>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Feedback panel */}
            <AnimatePresence>
              {showFeedback && selectedChoice !== null && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  {(() => {
                    const choice = scene.choices[selectedChoice];
                    const config = QUALITY_CONFIG[choice.quality];
                    const IconComp = config.icon;
                    return (
                      <div
                        className="rounded-xl px-4 py-4 mb-4 flex flex-col gap-3"
                        style={{
                          background: config.bg,
                          border: `1.5px solid ${config.border}`,
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <IconComp size={20} color={config.color} className="flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold mb-1" style={{ color: config.color }}>{config.label}</p>
                            <p className="text-sm leading-relaxed" style={{ color: 'hsl(var(--foreground))' }}>
                              {choice.feedback}
                            </p>
                          </div>
                        </div>

                        <button
                          onClick={handleNext}
                          className="self-end inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
                          style={{
                            background: 'var(--gradient-ocean)',
                            color: 'hsl(0 0% 100%)',
                            boxShadow: 'var(--shadow-md)',
                            transition: 'opacity 0.2s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >
                          {choice.next === 'result' ? 'Завершить' : 'Продолжить'}
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    );
                  })()}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DialogScene;
